import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { usePageTitle } from 'utils/pageTitle';
import { get } from 'data/services/apiService';
import { getApiUrl } from 'data/services/apiConstants';
import { describeEmail, getDatasetLabel } from 'utils/adminEmailUtils';
import AdminLayout from 'pages/_rd_dashboard/AdminLayout';
import EmailDetailsModal from 'pages/_rd_dashboard/AdminEmailTracking/EmailDetailsModal';

import Spinner from 'components/Spinner';
import Paginator from 'components/Paginator';

import './AdminNotifications.css';

const PER_PAGE = 10;

// Both come from the email log (queued_emails.type): alerts are for new items, notifications for updates
export const TABS = [
	{
		type: 'alert',
		label: 'Alerts',
		hint: 'Emails about new items found for a property, e.g. a new violation, complaint or permit.',
	},
	{
		type: 'notification',
		label: 'Notifications',
		hint: 'Emails about updates to items the user already had, e.g. a status or hearing date change.',
	},
];

const STATUS_BADGES = {
	pending: 'bg-warning',
	sent: 'bg-success',
	sent_digest: 'bg-info',
	failed: 'bg-danger',
	bounced: 'bg-secondary',
};

const AdminNotifications = () => {
	usePageTitle('Admin | Alerts & Notifications');
	const dispatch = useDispatch();
	const authToken = useSelector((state) => state.user.authToken);

	const [type, setType] = useState(TABS[0].type);
	const [page, setPage] = useState(1);
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [result, setResult] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [selectedEmail, setSelectedEmail] = useState(null);

	useEffect(() => {
		let isCurrent = true;
		const params = new URLSearchParams({
			type,
			page,
			per_page: PER_PAGE,
			sort_by: 'created_at',
			sort_order: 'desc',
		});
		if (search) {
			params.set('search', search);
		}
		setIsLoading(true);
		setHasError(false);
		get({
			url: `${getApiUrl()}/api/admin/email-tracking?${params.toString()}`,
			authToken,
			dispatch,
		})
			.then((response) => {
				if (isCurrent) setResult(response);
			})
			.catch(() => {
				if (isCurrent) {
					setResult(null);
					setHasError(true);
				}
			})
			.finally(() => {
				if (isCurrent) setIsLoading(false);
			});
		// ignore responses for a tab/page the admin has already left
		return () => {
			isCurrent = false;
		};
	}, [type, page, search, authToken, dispatch]);

	const changeTab = (nextType) => {
		setType(nextType);
		setPage(1);
	};

	const submitSearch = (e) => {
		e.preventDefault();
		setSearch(searchInput.trim());
		setPage(1);
	};

	const rows = result?.data || [];
	const activeTab = TABS.find((tab) => tab.type === type);

	return (
		<AdminLayout>
			<div className="dashboard-portfolio admin-alerts-notifications">
				<h1>Alerts &amp; Notifications</h1>

				<div className="admin-an-toolbar">
					<div className="admin-an-tabs" role="tablist">
						{TABS.map((tab) => (
							<button
								key={tab.type}
								type="button"
								role="tab"
								aria-selected={tab.type === type}
								className={`admin-an-tab ${tab.type === type ? 'active' : ''}`}
								onClick={() => changeTab(tab.type)}
							>
								{tab.label}
							</button>
						))}
					</div>
					<form className="admin-an-search" onSubmit={submitSearch}>
						<input
							type="search"
							className="form-control"
							placeholder="Search user, email or subject"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							aria-label="Search alerts and notifications"
						/>
						<button type="submit" className="btn btn-dark">Search</button>
					</form>
				</div>
				<p className="admin-an-hint">
					{activeTab.hint}
					{result?.total !== undefined && ` ${result.total.toLocaleString()} total.`}
				</p>

				{isLoading ? <Spinner /> :
					<div className="pb-4">
						{hasError && (
							<div className="alert alert-danger">Could not load {activeTab.label.toLowerCase()}. Please try again.</div>
						)}
						<div className="dashboard-table-wrapper">
							<table className="table table-striped table-rounded">
								<thead><tr>
									<th>User</th>
									<th>Property</th>
									<th>Dataset</th>
									<th>Email</th>
									<th>Status</th>
									<th>Time</th>
									<th aria-label="Actions" />
								</tr></thead>
								<tbody>
									{!hasError && rows.length === 0 && (
										<tr><td colSpan={7} className="text-center text-muted">No {activeTab.label.toLowerCase()} found.</td></tr>
									)}
									{rows.map((email) => (
										<tr key={email.id}>
											<td className="dashboard-username-cell">
												<div>{email.user?.name || '-'}</div>
												<div className="admin-an-muted">{email.to_email || email.user?.email || ''}</div>
											</td>
											<td className="dashboard-table-property-address-cell">{email.property?.address || '-'}</td>
											<td>{getDatasetLabel(email.class)}</td>
											<td className="break-anywhere">
												<div>{email.subject || '-'}</div>
												<div className="admin-an-muted">{describeEmail(email)}</div>
											</td>
											<td>
												<span className={`badge ${STATUS_BADGES[email.status] || 'bg-secondary'}`}>{email.status || '-'}</span>
											</td>
											<td className="dashboard-date-time-cell">{email.sent_at || email.created_at || '-'}</td>
											<td>
												<button type="button" className="btn btn-sm btn-outline-dark" onClick={() => setSelectedEmail(email)}>
													View
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<Paginator
							currentPage={result?.current_page || 1}
							totalPages={result?.last_page || 1}
							setPage={setPage}
						/>
					</div>
				}
			</div>
			{selectedEmail && (
				<EmailDetailsModal email={selectedEmail} onClose={() => setSelectedEmail(null)} />
			)}
		</AdminLayout>
	);
};

export default AdminNotifications;
